<template>
  <div class="home-page">
    <PortalHeader />

    <main class="main-content">
      <!-- Hero -->
      <section class="hero-section">
        <div class="hero-bg hero-bg-1"></div>
        <div class="hero-bg hero-bg-2"></div>
        <div class="hero-bg hero-bg-3"></div>
        <div class="container hero-layout">
          <div class="hero-left">
            <h1 class="hero-title">
              <span class="animate-fade-up">笔芯科研 · 论文AI降重与降AIGC率平台</span>
              <span class="highlight animate-fade-up delay-1">支持知网 / 维普 / 万方</span>
            </h1>
            <p class="hero-subtitle animate-fade-up delay-2">面向学位论文与期刊稿件，降重复率、降AIGC与双降处理，段落级语义重构，适配主流查重与AIGC检测体系</p>

            <ul class="hero-benefits animate-fade-up delay-2">
              <li v-for="item in heroBenefits" :key="item">
                <i class="bi bi-check-circle-fill"></i>{{ item }}
              </li>
            </ul>

            <div class="hero-block animate-fade-up delay-3">
              <div class="block-title">支持平台</div>
              <div class="tag-row">
                <span v-for="(p, i) in platforms" :key="p" :style="{ animationDelay: `${0.4 + i * 0.06}s` }" class="tag-item">{{ p }}</span>
              </div>
            </div>

            <div class="stat-row animate-fade-up delay-4">
              <div class="stat-item">
                <strong class="count-up">10万+</strong>
                <span>累计处理文档</span>
              </div>
              <div class="stat-divider"></div>
              <div class="stat-item">
                <strong class="gradient-text">&lt;10%</strong>
                <span>优化后AIGC率参考</span>
              </div>
              <div class="stat-divider"></div>
              <div class="stat-item">
                <strong>4</strong>
                <span>核心服务类型</span>
              </div>
            </div>

            <div class="hero-actions animate-fade-up delay-5">
              <router-link to="/workbench" class="btn-primary">立即开始 <i class="bi bi-arrow-right"></i></router-link>
              <a href="#features" class="btn-outline">了解更多</a>
            </div>
          </div>

          <div id="workbench" class="hero-right animate-fade-right delay-3">
            <HeroWorkbench />
          </div>
        </div>
      </section>

      <!-- 平台滚动条 -->
      <section class="marquee-section">
        <div class="marquee-track">
          <div class="marquee-group">
            <span v-for="item in marqueeItems" :key="'a-' + item" class="marquee-item">{{ item }}</span>
          </div>
          <div class="marquee-group" aria-hidden="true">
            <span v-for="item in marqueeItems" :key="'b-' + item" class="marquee-item">{{ item }}</span>
          </div>
        </div>
      </section>

      <!-- 平台介绍 -->
      <section class="intro-section container">
        <h2 v-reveal class="section-title">
          专业的 <span class="gradient-text">降重降AI</span> 平台
        </h2>
        <p v-reveal="{ delay: 80 }" class="section-desc">
          笔芯科研 聚焦四大核心能力：降重复率、降AIGC率、双降、学术润色。统一字数池计费，按服务类型消耗不同倍率，简单透明。
        </p>
        <nav v-reveal="{ delay: 100 }" class="section-nav-links" aria-label="相关页面">
          <router-link to="/pricing">查看套餐价格</router-link>
          <router-link to="/faq">常见问题与帮助</router-link>
          <router-link to="/about">关于笔芯科研</router-link>
        </nav>

        <div class="quick-entries quick-entries-4">
          <router-link
            v-for="(item, index) in quickEntries"
            :key="item.key"
            v-reveal="{ delay: index * 100 }"
            to="/workbench"
            class="entry-card">
            <img class="entry-icon-img" :src="item.iconSrc" :alt="item.title" />
            <span class="entry-title">{{ item.title }}</span>
            <span class="entry-desc">{{ item.desc }}</span>
            <span class="entry-link">立即使用 <i class="bi bi-arrow-right"></i></span>
          </router-link>
        </div>
      </section>

      <!-- 使用流程 -->
      <section class="workflow-section">
        <div class="container">
          <h2 v-reveal class="section-title">四步完成处理</h2>
          <p v-reveal="{ delay: 80 }" class="section-desc">无需复杂配置，从提交到下载全程在线完成</p>
          <div class="workflow-grid">
            <div
              v-for="(step, index) in workflowSteps"
              :key="step.title"
              v-reveal="{ delay: index * 100 }"
              class="workflow-card">
              <div class="workflow-step">{{ index + 1 }}</div>
              <div class="workflow-icon"><i :class="step.icon"></i></div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 适用场景 -->
      <section class="scenarios-section container">
        <h2 v-reveal class="section-title">适用场景</h2>
        <p v-reveal="{ delay: 80 }" class="section-desc">覆盖科研写作与修改的关键环节，按需选择服务类型</p>
        <div class="scenario-grid">
          <div
            v-for="(item, index) in scenarios"
            :key="item.title"
            v-reveal="{ delay: index * 100 }"
            class="scenario-card">
            <div class="scenario-icon"><i :class="item.icon"></i></div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
            <div class="scenario-tags">
              <span v-for="tag in item.tags" :key="tag">{{ tag }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 核心功能 -->
      <section id="features" class="features-section">
        <div class="container">
          <h2 v-reveal class="section-title">核心功能</h2>
          <p v-reveal="{ delay: 80 }" class="section-desc">围绕学术场景打造，支持文本粘贴与 docx 上传两种提交方式</p>
          <div class="feature-grid">
            <div
              v-for="(item, index) in features"
              :key="item.title"
              v-reveal="{ delay: index * 120 }"
              class="feature-card">
              <div class="feature-icon-wrap">
                <i :class="item.icon"></i>
              </div>
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 产品亮点 -->
      <section class="highlights-section">
        <div class="container">
          <h2 v-reveal class="section-title">产品亮点</h2>
          <div class="highlights-grid">
            <div
              v-for="(item, index) in highlights"
              :key="item.title"
              v-reveal="{ delay: index * 80 }"
              class="highlight-card">
              <i :class="item.icon"></i>
              <div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 价格 -->
      <section id="pricing" class="pricing-section container">
        <h2 v-reveal class="section-title">字数套餐</h2>
        <p v-reveal="{ delay: 80 }" class="section-desc">
          按字数购买，充值到统一账户。降重、降 AIGC、双降、润色按字数消耗，透明计费。
          <router-link to="/pricing" class="inline-link">查看完整套餐说明</router-link>
        </p>
        <div class="pricing-grid">
          <div
            v-for="(plan, index) in pricingPlans"
            :key="plan.productId"
            v-reveal="{ delay: index * 120, direction: 'scale' }"
            class="pricing-card"
            :class="{ featured: plan.featured }">
            <div v-if="plan.badge" class="badge">{{ plan.badge }}</div>
            <h3>{{ plan.name }}</h3>
            <div class="price"><span>¥</span>{{ plan.price }}</div>
            <p class="quota">{{ plan.quotaLabel }} 字</p>
            <ul>
              <li v-for="(line, idx) in plan.features" :key="idx">{{ line }}</li>
            </ul>
            <router-link to="/pricing" class="btn-primary block">立即购买</router-link>
          </div>
        </div>
      </section>

      <!-- 用户评价 -->
      <section class="testimonials-section">
        <div class="container">
          <h2 v-reveal class="section-title">用户评价</h2>
          <p v-reveal="{ delay: 80 }" class="section-desc">以下为部分用户的使用感受，效果因原文与检测平台而异</p>
          <div class="testimonials-grid">
            <article
              v-for="(item, index) in testimonials"
              :key="item.author"
              v-reveal="{ delay: index * 80 }"
              class="testimonial-card">
              <blockquote>{{ item.quote }}</blockquote>
              <footer>
                <strong>{{ item.author }}</strong>
                <span>{{ item.meta }}</span>
              </footer>
            </article>
          </div>
        </div>
      </section>

      <!-- 对比 -->
      <section class="compare-section">
        <div class="container">
          <h2 v-reveal class="section-title">为什么选择笔芯科研</h2>
          <p v-reveal="{ delay: 80 }" class="section-desc compare-desc">
            同样做降重降 AI，体验差异往往体现在改写质量、平台适配与处理效率上
          </p>
          <div class="compare-grid">
            <div v-reveal="{ direction: 'left' }" class="compare-card bad">
              <h3>其他平台</h3>
              <ul>
                <li><i class="bi bi-x-circle"></i> 降重随意改词，段落逻辑断裂</li>
                <li><i class="bi bi-x-circle"></i> 降 AI 越改越高，反复返工</li>
                <li><i class="bi bi-x-circle"></i> 一段一段手动复制粘贴</li>
                <li><i class="bi bi-x-circle"></i> 排队久、长文档容易超时</li>
              </ul>
            </div>
            <div v-reveal="{ direction: 'right', delay: 120 }" class="compare-card good">
              <h3>笔芯科研</h3>
              <ul>
                <li><i class="bi bi-check-circle-fill"></i> 改写后语义通顺，可读性更好</li>
                <li><i class="bi bi-check-circle-fill"></i> 知网/维普AIGC可压至较低区间</li>
                <li><i class="bi bi-check-circle-fill"></i> docx 全文上传，段落级批量处理</li>
                <li><i class="bi bi-check-circle-fill"></i> 异步队列，处理完直接下载</li>
              </ul>
            </div>
          </div>
          <div v-reveal="{ delay: 160 }" class="compare-cta">
            <router-link to="/workbench" class="btn-primary">立即体验 <i class="bi bi-arrow-right"></i></router-link>
          </div>
        </div>
      </section>

      <!-- 免费答辩 PPT（SEO） -->
      <section id="free-defense-ppt" class="free-ppt-section container">
        <h2 v-reveal class="section-title">免费生成答辩PPT</h2>
        <p v-reveal="{ delay: 60 }" class="section-desc">
          笔芯科研 提供<strong>免费生成答辩PPT</strong>能力：上传开题报告、中期检查、毕业论文或答辩材料（docx、pdf、txt 等），
          按开题答辩、中期答辩、毕业答辩等场景自动生成结构化演示文稿，可下载 .pptx 在 WPS 或 PowerPoint 中编辑。
          新用户首次生成不扣积分，适合答辩前快速搭框架、省排版时间。
        </p>
        <ul v-reveal="{ delay: 80 }" class="free-ppt-list">
          <li>支持开题答辩、中期答辩、毕业答辩、工作总结、研究报告等多类模板风格</li>
          <li>可选演讲备注与自动配图，生成记录在线查看进度与下载</li>
          <li>积分账户全站通用，降重、降 AIGC、检测、润色与答辩 PPT 同一账户</li>
        </ul>
        <p v-reveal="{ delay: 100 }" class="free-ppt-cta">
          <router-link to="/ppt" class="btn-ppt-free">免费生成答辩PPT <i class="bi bi-easel"></i></router-link>
        </p>
      </section>

      <!-- FAQ -->
      <section id="faq" class="faq-section container">
        <h2 v-reveal class="section-title">常见问题</h2>
        <p v-reveal="{ delay: 60 }" class="section-desc">
          关于降重效果、降 AIGC、计费与安全的高频问题。更多说明见
          <router-link to="/faq" class="inline-link">常见问题页</router-link>
          与
          <router-link to="/pricing" class="inline-link">套餐价格</router-link>。
        </p>
        <div class="faq-list">
          <div
            v-for="(item, index) in faqs"
            :key="item.q"
            v-reveal="{ delay: index * 80 }"
            class="faq-item">
            <h3>{{ item.q }}</h3>
            <p>{{ item.a }}</p>
          </div>
        </div>
        <p v-reveal="{ delay: 120 }" class="faq-more">
          <router-link to="/faq">查看更多常见问题 <i class="bi bi-arrow-right"></i></router-link>
        </p>
      </section>

      <!-- CTA -->
      <section id="about" class="cta-section">
        <div v-reveal="{ direction: 'scale' }" class="container cta-inner">
          <h2>准备好开始了吗？</h2>
          <p>注册即送体验字数，四大服务统一账户，随买随用</p>
          <div class="cta-actions">
            <router-link to="/workbench" class="btn-primary">马上开始 <i class="bi bi-arrow-right"></i></router-link>
            <router-link to="/ppt" class="btn-ppt-free">免费生成答辩PPT <i class="bi bi-easel"></i></router-link>
          </div>
        </div>
      </section>
    </main>

    <PortalFooter />
  </div>
</template>

<script>
import PortalHeader from '@/components/portal/PortalHeader.vue'
import PortalFooter from '@/components/portal/PortalFooter.vue'
import HeroWorkbench from '@/components/portal/HeroWorkbench.vue'

import jcSvg from '@/assets/svg/jc-svg.svg'
import aigcSvg from '@/assets/svg/aigc-svg.svg'
import sjSvg from '@/assets/svg/sj-svg.svg'
import xsrsSvg from '@/assets/svg/xsrs-svg.svg'
import { RECHARGE_PACKAGES } from '@/constants/rechargePackages'
import { MARKETING_FAQS } from '@/constants/marketingFaq'

export default {
  name: 'IndexView',
  components: {
    PortalHeader,
    PortalFooter,
    HeroWorkbench
  },
  computed: {
    pricingPlans() {
      return RECHARGE_PACKAGES.slice(0, 3).map(plan => ({
        ...plan,
        quotaLabel: Number(plan.quotaAmount).toLocaleString(),
        features: [plan.description, plan.perThousand, '四服务通用，永久有效']
      }))
    }
  },
  data() {
    return {
      platforms: ['知网', '维普', '万方', '大雅', '格子达'],
      heroBenefits: [
        '支持粘贴文本与 docx 全文处理',
        '可附官方检测报告，仅改标红段落',
        '中英文均可，按实际字数扣点'
      ],
      marqueeItems: [
        '知网适配', '维普适配', '万方适配', '大雅适配', '格子达适配',
        '降重复率', '降AIGC率', '双降', '学术润色', 'docx 上传', '段落级处理'
      ],
      quickEntries: [
        { key: 'DEDUP', iconSrc: jcSvg, title: '降重复率', desc: '降低查重重复率，语义通顺' },
        { key: 'AIGC', iconSrc: aigcSvg, title: '降AIGC率', desc: '降低 AI 检出率，更自然' },
        { key: 'DUAL', iconSrc: sjSvg, title: '双降', desc: '同时降重 + 降AIGC' },
        { key: 'POLISH', iconSrc: xsrsSvg, title: '学术润色', desc: '提升科研学术专业表达' }
      ],
      workflowSteps: [
        { icon: 'bi bi-ui-checks-grid', title: '选择服务', desc: '按需求选择降重、降AIGC、双降或润色，并选择语言与检测平台' },
        { icon: 'bi bi-file-earmark-text', title: '提交内容', desc: '粘贴段落文本，或上传 docx；降AIGC 可附官方检测报告' },
        { icon: 'bi bi-cpu', title: '智能处理', desc: '后台异步队列逐段改写，实时查看进度与消耗点数' },
        { icon: 'bi bi-download', title: '获取结果', desc: '文本模式直接复制，文件模式下载处理后的 docx' }
      ],
      scenarios: [
        {
          icon: 'bi bi-mortarboard',
          title: '毕业科研定稿',
          desc: '提交前最后一轮降重与降 AI，降低答辩前查重风险。',
          tags: ['双降', '全文 docx']
        },
        {
          icon: 'bi bi-journal-text',
          title: '期刊投稿润色',
          desc: '学术润色优化表达，降重服务适配编辑部查重系统。',
          tags: ['润色', '降重']
        },
        {
          icon: 'bi bi-clipboard-check',
          title: '官方报告精改',
          desc: '上传知网/维普等检测报告，仅处理标红段落，改动更精准。',
          tags: ['降AIGC', '标红段落']
        },
        {
          icon: 'bi bi-translate',
          title: '英文摘要优化',
          desc: '英文降重与润色，适配英文检测与学术写作规范。',
          tags: ['英文', '摘要']
        }
      ],
      highlights: [
        { icon: 'bi bi-shield-lock', title: '隐私保护', desc: '文档加密传输，仅用于当次处理，不对外公开' },
        { icon: 'bi bi-sliders', title: '多平台适配', desc: '知网、维普、万方、大雅、格子达策略可切换' },
        { icon: 'bi bi-coin', title: '透明计费', desc: '按字数 × 服务倍率扣点，充值套餐永久有效' },
        { icon: 'bi bi-arrow-repeat', title: '7 天免费改写', desc: '文件任务完成后 7 天内段落可无限重新生成' },
        { icon: 'bi bi-list-check', title: '段落级处理', desc: '长文档按段改写，跳过图表题注等无需修改部分' },
        { icon: 'bi bi-lightning-charge', title: '异步队列', desc: '提交即返回，后台处理不阻塞，支持任务续跑' }
      ],
      features: [
        {
          icon: 'bi bi-arrow-repeat',
          title: '降重复率',
          desc: '智能改写表达，有效降低与已有文献的重复率，适配主流查重平台。'
        },
        {
          icon: 'bi bi-robot',
          title: '降AIGC率',
          desc: '优化 AI 痕迹明显的段落，使文本更接近人工写作风格。'
        },
        {
          icon: 'bi bi-layers',
          title: '双降',
          desc: '一次提交同时完成降重与降 AIGC，适合双重检测场景。'
        },
        {
          icon: 'bi bi-pencil-square',
          title: '学术润色',
          desc: '提升学术用语规范性与逻辑连贯性，支持中文与英文润色。'
        }
      ],
      testimonials: [
        {
          author: '某985高校 · 硕士',
          meta: '双降 · 知网',
          quote: '定稿前试了两段，重复率和AIGC都有下降，改完读起来还算顺，自己再通读一遍就能交。'
        },
        {
          author: '某211高校 · 本科',
          meta: '降AIGC· 粘贴文本',
          quote: '标红段落单独粘贴进去改，比整篇丢进去放心。有些句子改动不大，专业词基本还在。'
        },
        {
          author: '期刊投稿用户',
          meta: '学术润色 · 英文',
          quote: '英文摘要润色后语法顺了一些，后面又做了降重。两步分开做，比一次堆在一起好控制。'
        },
        {
          author: '在职读研',
          meta: '降重 · 维普',
          quote: '维普那版重复率降下来了，术语没乱改太多。字数套餐不过期，断断续续写论文也能用。'
        },
        {
          author: '入门版用户',
          meta: '降重 · 试改',
          quote: '先买了入门版试几段，觉得还行再充的。提交前能看到大概扣多少字，心里比较有数。'
        },
        {
          author: 'docx 全文用户',
          meta: '双降 · 长文档',
          quote: '整篇 Word 上传后后台慢慢跑，不用一直盯着。处理完回工作台下载就行，段落结构和目录都还在。'
        }
      ],
      faqs: MARKETING_FAQS
    }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  background: var(--bg-page);
}

.main-content {
  padding-top: calc(var(--header-height) + 24px);
}

.hero-section {
  position: relative;
  padding: 40px 0 64px;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(1px);
}

.hero-bg-1 {
  top: 40px;
  left: 8%;
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(25, 118, 210, 0.28) 0%, rgba(25, 118, 210, 0) 70%);
  animation: float-orb 12s ease-in-out infinite;
}

.hero-bg-2 {
  top: 120px;
  right: 5%;
  width: 280px;
  height: 280px;
  background: radial-gradient(circle, rgba(25, 118, 210, 0.18) 0%, rgba(25, 118, 210, 0) 70%);
  animation: float-orb 9s ease-in-out infinite reverse;
}

.hero-bg-3 {
  bottom: 0;
  left: 35%;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(66, 165, 245, 0.15) 0%, rgba(66, 165, 245, 0) 70%);
  animation: float-orb 14s ease-in-out infinite;
  animation-delay: -3s;
}

.hero-layout {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 560px;
  gap: 48px;
  align-items: center;
}

.hero-title {
  margin: 0;
  font-size: 48px;
  line-height: 1.15;
  font-weight: 800;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hero-title .highlight {
  color: var(--primary);
}

.hero-subtitle {
  margin: 20px 0 0;
  font-size: 18px;
  line-height: 1.7;
  color: var(--text-secondary);
  max-width: 520px;
}

.hero-benefits {
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 520px;
}

.hero-benefits li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: var(--text-primary);
}

.hero-benefits i {
  color: #22c55e;
  font-size: 16px;
  flex-shrink: 0;
}

.hero-block {
  margin-top: 28px;
}

.block-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 40px;
}

.intro-section {
  padding: 48px 24px 64px;
}

.marquee-section {
  overflow: hidden;
  border-block: 1px solid #dbeafe;
  background: linear-gradient(90deg, #f5faff 0%, #fff 50%, #f5faff 100%);
  padding: 14px 0;
}

.marquee-track {
  display: flex;
  width: max-content;
  animation: marquee-scroll 32s linear infinite;
}

.marquee-group {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 12px;
}

.marquee-item {
  flex-shrink: 0;
  padding: 8px 18px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #dbeafe;
  color: var(--text-secondary);
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.06);
}

@keyframes marquee-scroll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}

.quick-entries {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.quick-entries-4 {
  grid-template-columns: repeat(4, 1fr);
}

.hero-right {
  width: 100%;
}

.tag-item {
  animation: fade-in-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.entry-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border-radius: var(--radius-md);
  background: #fff;
  border: 1px solid var(--primary-border);
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.06);
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease, border-color 0.35s ease;
}

.entry-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-card);
  border-color: var(--primary);
}

.entry-card:hover .entry-icon-img {
  transform: scale(1.08);
}

.entry-icon-img {
  width: 36px;
  height: 36px;
  object-fit: contain;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.entry-link {
  margin-top: 4px;
  font-size: 13px;
  color: var(--primary);
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.entry-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--primary-light);
  color: var(--primary);
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.entry-title {
  font-size: 20px;
  font-weight: 600;
}

.entry-desc {
  font-size: 14px;
  color: var(--text-secondary);
  flex: 1;
}

.workflow-section {
  padding: 64px 0;
  background: #fff;
}

.workflow-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.workflow-card {
  position: relative;
  padding: 28px 22px;
  border-radius: var(--radius-md);
  background: var(--bg-soft);
  border: 1px solid #dbeafe;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.workflow-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-card);
}

.workflow-step {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--primary-light);
  color: var(--primary);
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.workflow-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.workflow-icon i {
  font-size: 22px;
  color: var(--primary);
}

.workflow-card h3 {
  margin: 0 0 8px;
  font-size: 18px;
}

.workflow-card p {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: var(--text-secondary);
}

.scenarios-section {
  padding: 64px 24px;
}

.scenario-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.scenario-card {
  padding: 24px;
  border-radius: var(--radius-md);
  background: #fff;
  border: 1px solid #dbeafe;
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.05);
  transition: transform 0.3s ease, border-color 0.3s ease;
}

.scenario-card:hover {
  transform: translateY(-4px);
  border-color: var(--primary-border);
}

.scenario-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e3f2fd, #f0f7ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.scenario-icon i {
  font-size: 20px;
  color: var(--primary);
}

.scenario-card h3 {
  margin: 0 0 8px;
  font-size: 17px;
}

.scenario-card p {
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.65;
  color: var(--text-secondary);
}

.scenario-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.scenario-tags span {
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--primary-light);
  color: var(--primary);
  font-size: 12px;
}

.features-section {
  padding: 64px 0;
  background: var(--bg-soft);
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.feature-card {
  background: #fff;
  border-radius: var(--radius-md);
  padding: 28px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease;
  border: 1px solid transparent;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-card);
  border-color: rgba(25, 118, 210, 0.15);
}

.feature-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, #e3f2fd, #f0f7ff);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.35s ease, background 0.35s ease;
}

.feature-card:hover .feature-icon-wrap {
  transform: scale(1.08);
  background: linear-gradient(135deg, #e0e7ff, #ede9fe);
}

.feature-card i {
  font-size: 28px;
  color: var(--primary);
  transition: transform 0.35s ease;
}

.feature-card:hover i {
  transform: rotate(-8deg);
}

.feature-card h3 {
  margin: 16px 0 10px;
  font-size: 22px;
}

.feature-card p {
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 15px;
}

.highlights-section {
  padding: 64px 0;
  background: linear-gradient(180deg, #fff 0%, var(--bg-soft) 100%);
}

.highlights-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.highlight-card {
  display: flex;
  gap: 16px;
  padding: 22px;
  border-radius: var(--radius-md);
  background: #fff;
  border: 1px solid #dbeafe;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.highlight-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(25, 118, 210, 0.08);
}

.highlight-card > i {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--primary-light);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.highlight-card h3 {
  margin: 0 0 6px;
  font-size: 16px;
}

.highlight-card p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-secondary);
}

.pricing-section {
  padding: 80px 24px;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.pricing-card {
  position: relative;
  background: #fff;
  border-radius: var(--radius-lg);
  padding: 32px 28px;
  border: 1px solid #e5e7eb;
  text-align: center;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease;
}

.pricing-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.08);
}

.pricing-card.featured {
  border-color: var(--primary);
  box-shadow: 0 12px 40px rgba(25, 118, 210, 0.15);
  transform: scale(1.03);
}

.pricing-card.featured:hover {
  transform: scale(1.03) translateY(-6px);
  box-shadow: 0 20px 56px rgba(25, 118, 210, 0.22);
}

.badge {
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--primary);
  color: #fff;
  font-size: 12px;
  animation: pulse-ring 2.5s ease-out infinite;
}

.pricing-card h3 {
  margin: 0 0 16px;
  font-size: 20px;
}

.price {
  font-size: 42px;
  font-weight: 700;
  color: var(--primary);
}

.price span {
  font-size: 20px;
  margin-right: 4px;
}

.quota {
  color: var(--text-secondary);
  margin: 8px 0 20px;
}

.pricing-card ul {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  text-align: left;
}

.pricing-card li {
  padding: 8px 0;
  color: var(--text-secondary);
  font-size: 14px;
  border-bottom: 1px dashed #eee;
}

.pricing-card li:last-child {
  border-bottom: none;
}

.btn-primary.block {
  width: 100%;
}

.section-nav-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 20px;
  margin: 0 0 32px;
}

.section-nav-links a {
  font-size: 15px;
  color: var(--primary);
  text-decoration: none;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(25, 118, 210, 0.08);
  transition: background 0.2s ease, color 0.2s ease;
}

.section-nav-links a:hover {
  background: rgba(25, 118, 210, 0.16);
  color: var(--primary-dark, #1565c0);
}

.inline-link {
  color: var(--primary);
  text-decoration: none;
  margin-left: 4px;
}

.inline-link:hover {
  text-decoration: underline;
}

.testimonials-section {
  padding: 72px 0;
  background: #fff;
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;
}

.testimonial-card {
  background: var(--bg-soft);
  border: 1px solid #eee;
  border-radius: var(--radius-lg);
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.testimonial-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-card);
}

.testimonial-card blockquote {
  margin: 0;
  flex: 1;
  font-size: 15px;
  line-height: 1.75;
  color: var(--text-primary);
}

.testimonial-card footer {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: var(--text-secondary);
}

.testimonial-card footer strong {
  color: var(--text-primary);
  font-size: 14px;
}

.compare-desc {
  text-align: center;
  margin-bottom: 32px;
}

.compare-cta {
  text-align: center;
  margin-top: 40px;
}

.compare-section {
  padding: 64px 0;
  background: var(--bg-soft);
}

.compare-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  max-width: 960px;
  margin: 0 auto;
}

.compare-card {
  background: #fff;
  border-radius: var(--radius-lg);
  padding: 36px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
}

.compare-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-card);
}

.compare-card.good {
  border-color: rgba(34, 197, 94, 0.2);
}

.compare-card.bad {
  border-color: rgba(239, 68, 68, 0.12);
}

.compare-card h3 {
  text-align: center;
  font-size: 24px;
  margin: 0 0 24px;
}

.compare-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.compare-card li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  font-size: 16px;
  line-height: 1.5;
}

.compare-card.bad i {
  color: #ef4444;
  font-size: 20px;
}

.compare-card.good i {
  color: #22c55e;
  font-size: 20px;
}

.faq-section {
  padding: 80px 24px;
}

.faq-list {
  max-width: 880px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.faq-item {
  background: #fff;
  border-radius: var(--radius-md);
  padding: 24px;
  border: 1px solid #eee;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}

.faq-item:hover {
  transform: translateX(6px);
  border-color: rgba(25, 118, 210, 0.25);
  box-shadow: 0 8px 24px rgba(25, 118, 210, 0.08);
}

.faq-item h3 {
  margin: 0 0 10px;
  font-size: 18px;
}

.faq-item p {
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 15px;
}

.faq-more {
  text-align: center;
  margin: 32px 0 0;
}

.faq-more a {
  color: var(--primary);
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
}

.faq-more a:hover {
  text-decoration: underline;
}

.cta-section {
  padding: 72px 0 48px;
}

.cta-inner {
  text-align: center;
  padding: 56px 24px;
  border-radius: 24px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
  position: relative;
  overflow: hidden;
}

.cta-inner::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 20% 50%, rgba(25, 118, 210, 0.12), transparent 50%),
    radial-gradient(circle at 80% 50%, rgba(66, 165, 245, 0.1), transparent 50%);
  animation: float-orb 10s ease-in-out infinite;
  pointer-events: none;
}

.cta-inner h2,
.cta-inner p,
.cta-inner a,
.cta-inner .cta-actions {
  position: relative;
  z-index: 1;
}

.cta-inner h2 {
  margin: 0 0 12px;
  font-size: 32px;
}

.cta-inner p {
  margin: 0 0 28px;
  color: var(--text-secondary);
  font-size: 16px;
}

.cta-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: center;
  align-items: center;
}

.btn-ppt-free {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 600;
}

.free-ppt-section {
  padding: 56px 0 24px;
}

.free-ppt-list {
  margin: 20px auto 0;
  max-width: 720px;
  padding-left: 1.25rem;
  color: var(--text-secondary);
  line-height: 1.75;
  font-size: 15px;
}

.free-ppt-list li {
  margin-bottom: 8px;
}

.free-ppt-cta {
  text-align: center;
  margin: 28px 0 0;
}

@media (max-width: 1100px) {
  .hero-layout {
    grid-template-columns: 1fr;
  }

  .hero-right {
    justify-self: center;
    width: 100%;
    max-width: 560px;
  }

  .quick-entries-4,
  .workflow-grid,
  .scenario-grid,
  .feature-grid,
  .testimonials-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 960px) {
  .hero-title {
    font-size: 36px;
  }

  .quick-entries,
  .quick-entries-4,
  .workflow-grid,
  .scenario-grid,
  .feature-grid,
  .highlights-grid,
  .pricing-grid,
  .compare-grid,
  .testimonials-grid {
    grid-template-columns: 1fr;
  }

  .pricing-card.featured {
    transform: none;
  }
}
</style>
